import { DESCRIPTION_STREAM_DONE_MARKER } from '@revelio/iso-utility';

import { dashMetaFetch } from '../../../shared/api/dash-meta-fetch';
import { getAiSearchCsrfToken } from '../filters/ai-search/ai-search-api-request';

const stripDoneMarker = (text: string): string =>
  text.endsWith(DESCRIPTION_STREAM_DONE_MARKER)
    ? text.slice(0, -DESCRIPTION_STREAM_DONE_MARKER.length)
    : text;

export type DescriptionType = 'work_experience' | 'job_posting';

/**
 * Streaming variant: posts to the streaming endpoint and invokes `onChunk` with
 * the accumulated text as deltas arrive, resolving with the full text. Lets the
 * caller render markdown progressively instead of waiting for the whole result.
 */
export const formatDescriptionStreamRequest = async ({
  text,
  descriptionType,
  signal,
  onChunk,
}: {
  text: string;
  descriptionType?: DescriptionType;
  signal?: AbortSignal;
  onChunk?: (accumulatedText: string) => void;
}): Promise<string> => {
  const csrfToken = await getAiSearchCsrfToken();
  const response = await dashMetaFetch(
    '/api/ai-search/format-description/stream',
    {
      method: 'POST',
      headers: { 'x-surf-token-dash-meta-ai-search': csrfToken },
      body: JSON.stringify({
        text,
        ...(descriptionType ? { descriptionType } : {}),
      }),
      signal,
    }
  );

  if (!response.ok || !response.body) {
    const message = await response.text().catch(() => '');
    const error: Error & { status?: number } = new Error(
      message || 'Unknown error'
    );
    error.status = response.status;
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let accumulated = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    accumulated += decoder.decode(value, { stream: true });
    // The done-marker only arrives as the final chunk, so strip defensively so
    // progressive renders never flash it.
    onChunk?.(stripDoneMarker(accumulated));
  }
  accumulated += decoder.decode();

  // A complete stream ends with the done-marker. Its absence means the response
  // was truncated (mid-stream error after headers, or max_tokens) — surface that
  // as an error so the caller can fall back instead of treating the partial,
  // possibly broken Markdown as the full result.
  if (!accumulated.endsWith(DESCRIPTION_STREAM_DONE_MARKER)) {
    throw new Error('Incomplete description stream (no completion marker)');
  }

  return stripDoneMarker(accumulated);
};
